import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { connectInfiniteHits, Highlight } from 'react-instantsearch-dom';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { ReactComponent as RowInfoIcon } from '../../../assets/images/svg/sfh-info-secondary.svg';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import config from '../../../config';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { mediaBelow } from '../../../assets/styles/helper';

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
`;

const ProductRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid
    ${({ $selected }) => ($selected ? themeColors.colorPrimary : 'transparent')};
  border-radius: 6px;
  background: ${themeColors.colorBackgroundSecondary};
  width: 100%;
  flex-shrink: 0;
  ${mediaBelow(variables.breakpoints.bpMd)} {
    min-height: 64px;
  }
`;

const SelectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-align: left;
`;

const RightCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const InfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  padding: 4px;
  cursor: pointer;
  flex-shrink: 0;

  > svg {
    width: 18px;
    height: 18px;
    display: block;
  }
`;

const Radio = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${themeColors.colorPrimary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $selected }) =>
      $selected ? themeColors.colorPrimary : 'transparent'};
  }
  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: 16px;
    height: 16px;
  }
`;

const Thumb = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 4px;
`;

const ThumbPlaceholder = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  background: ${themeColors.colorBackgroundSecondary};
  flex-shrink: 0;
`;

const ProductName = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 18px;
  color: ${themeColors.colorTextPrimary};

  .ais-Highlight-highlighted {
    background: ${themeColors.colorWarningnBackground};
    color: ${themeColors.colorTextPrimary};
    font-weight: 700;
  }
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
    line-height: 16px;
    font-weight: 500;
  }
`;

const ProductWeight = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${themeColors.colorLabel};
  flex-shrink: 0;
  white-space: nowrap;
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  min-height: 0;
  padding: 16px;
  text-align: center;

  > svg {
    width: 40px;
    height: 40px;
  }
`;

const EmptyMessage = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 18px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 16px;
    line-height: 1.4;
    font-weight: 500;
  }
`;

const SupportLink = styled.a`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const ProductThumb = ({ productCode, rootUrl }) => {
  const [hasError, setHasError] = useState(false);
  if (hasError || !productCode) return <ThumbPlaceholder />;
  const src = config.algoliaSearch.imageCode
    .replace('{product_code}', productCode)
    .replace('{base_url}', rootUrl);
  return (
    <Thumb
      src={encodeURI(src)}
      alt=""
      loading="eager"
      decoding="async"
      onError={() => setHasError(true)}
    />
  );
};

ProductThumb.propTypes = {
  productCode: PropTypes.string,
  rootUrl: PropTypes.string,
};

// The SFH Algolia index isn't ranked by popularity, so order the SKU list
// client-side by each record's numeric Popularity (lower value = more popular).
// Records missing a usable Popularity sort to the end.
const getPopularity = (hit) => {
  const value = Number(hit?.Popularity);
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
};

const SellFromHomeProductList = ({
  hits,
  selectedCode,
  onSelect,
  onInfoClick,
  onHitsSettled,
}) => {
  const { t } = useTranslation();
  const { RootUrl } = useSelector(selectSettings);
  const phone = t('sellWizard.sfhWizard.phone');

  useEffect(() => {
    if (onHitsSettled) {
      onHitsSettled();
    }
  }, [hits, onHitsSettled]);

  const validHits = hits
    .filter((h) => h.Caption && h.Caption.trim())
    .sort((a, b) => getPopularity(a) - getPopularity(b));

  if (validHits.length === 0) {
    return (
      <EmptyState>
        <InfoIcon />
        <EmptyMessage>{t('sellWizard.sfhWizard.noResultsTitle')}</EmptyMessage>
        <SupportLink href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
          {t('sellWizard.sfhWizard.needHelp')} {phone}
        </SupportLink>
      </EmptyState>
    );
  }

  return (
    <ListWrap>
      {validHits.map((hit) => {
        const isSelected = selectedCode === hit.Code;
        return (
          <ProductRow key={hit.objectID} $selected={isSelected}>
            <SelectButton
              type="button"
              $selected={isSelected}
              onClick={() => onSelect(hit)}
            >
              <Radio $selected={isSelected} />
              <ProductThumb productCode={hit.Code} rootUrl={RootUrl} />
              <ProductName>
                <Highlight attribute="Caption" hit={hit} />
              </ProductName>
            </SelectButton>
            <RightCluster>
              <InfoButton
                type="button"
                onClick={() => {
                  onSelect(hit);
                  onInfoClick(hit);
                }}
                aria-label={t(
                  'sellWizard.sfhWizard.productDescription.heading',
                )}
                data-cy="button-sfh-product-info"
              >
                <RowInfoIcon />
              </InfoButton>
              <ProductWeight>{hit.WeightCaption}</ProductWeight>
            </RightCluster>
          </ProductRow>
        );
      })}
    </ListWrap>
  );
};

SellFromHomeProductList.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedCode: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onInfoClick: PropTypes.func.isRequired,
  onHitsSettled: PropTypes.func,
};

export default connectInfiniteHits(SellFromHomeProductList);
